import { useCallback, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { useNavigate } from "react-router";

import { USER_ROLE_ADMIN } from "@datanac/datanac-api-toolkit/dist/api-helper";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import { Box, Button, Grid, Stack, useMediaQuery } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { RmaApiHelper, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { getBudgetYears } from "components/Budget/helpers";
import DataFormSelect from "components/Core/DataFormSelect";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import DataGridContext from "core/DataGridContext";
import { getStorageValue, setStorageValue } from "core/useLocalStorage";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { formatNumber, formatPercentage, formatProducerName } from "utility/formatters";

import DropdownSelector from "../DropdownSelector";
import { BrokerIcon } from "../Icons/Icons";
import AgentAutoComplete, {
  dedupeBrokersByToken,
  getBrokerDisplayLabel,
  normalizeAgentTokens,
} from "./AgentAutoComplete";
import { exportRiskSummaryBrokerWorkbook } from "./exportRiskSummaryBrokerWorkbook";

const RISK_SUMMARY_BROKER_GRID_KEY = "risk-summary-broker";


function normalizeCommodityFilter(value) {
  if (value == null || value === "") {
    return null;
  }

  const trimmed = typeof value === "string" ? value.trim() : value;
  return trimmed || null;
}

function getReportRowKey(row) {
  return `${row.producer_token ?? ""}/${row.commodity_name ?? ""}`;
}

/** Normalize API percent to 0–1 ratio for storage, display, and sort. */
function toPercentProtectedRatio(value) {
  if (value == null || value === "") {
    return null;
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }

  // API may return 0–100; formatPercentage expects 0–1.
  if (numeric > 1 && numeric <= 100) {
    return numeric / 100;
  }

  return numeric;
}

/** Canonical % Protected on each row: always 0–1 ratio or null. */
function computePercentProtected(row) {
  const goal = Number(row?.marketing_goal_units);
  const hedged = Number(row?.unit_quantity_hedge_total);

  if (goal === 0) {
    return null;
  }

  if (Number.isFinite(goal) && goal !== 0) {
    const ratio = hedged / goal;
    if (Number.isFinite(ratio)) {
      return ratio;
    }
  }

  return toPercentProtectedRatio(row?.calc_percent_protected);
}

function getSortableValue(row, field) {
  if (field === "producer_token") {
    const displayName = formatProducerName(row);
    return displayName && displayName.trim() ? displayName.trim().toLowerCase() : null;
  }

  const value = row?.[field];
  if (value == null || value === "") {
    return null;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : value;
}

function sortReportRows(rows, sortConfig) {
  if (!Array.isArray(rows) || !sortConfig?.field) {
    return rows;
  }

  const direction = sortConfig.sort === "desc" ? "desc" : "asc";
  const { field } = sortConfig;

  return _.orderBy(
    rows,
    [(row) => getSortableValue(row, field) === null, (row) => getSortableValue(row, field)],
    ["asc", direction]
  );
}

export default function RiskSummaryBroker() {
  const { globalState, globalDispatch } = useContext(AppContext);
  const { getGridState, setGridState } = useContext(DataGridContext);
  const { enqueueSnackbar } = useSnackbar();

  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });

  const [currentYear, setCurrentYear] = useState(globalState?.crop_year);

  // LocalStorage keys
  const STORAGE_KEY = "search_commodity_name";

  // ---

  const [brokers, setBrokers] = useState(null);
  const [isLoadingBrokers, setIsLoadingBrokers] = useState(false);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const loadBrokers = async () => {
    try {
      if (globalState?.agent_token) {
        setIsLoadingBrokers(true);

        const _brokersFilter = {
          agent_token: globalState?.agent_token,
        };
        // This is agent function. Do not specify producer_token or you will get permissions error.
        const _apiConfig = {
          agent_token: globalState?.agent_token,
        };
        const _brokers = await UsersApiHelper.users_search("AgencyAgent", _brokersFilter, _apiConfig);
        setBrokers(_brokers);
        setIsLoadingBrokers(false);
      } else if (globalState?.user_role == USER_ROLE_ADMIN) {
        setIsLoadingBrokers(true);

        const _brokersFilter = {};
        // This is ADMIN function. Do not specify producer_token or agent_token or you will get permissions error.
        const _apiConfig = {};
        const _brokers = await UsersApiHelper.users_search("AgencyAgent", _brokersFilter, _apiConfig);

        setBrokers(_brokers);
        setIsLoadingBrokers(false);
      }
    } catch (err) {
      setIsLoadingBrokers(false);
      setBrokers(null);
      handleError(err);
    }
  };

  // --- --- ---

  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const _loadReportData = async () => {
    try {
      setIsLoading(true);

      const baseFilter = {
        agent_token: globalState?.agent_token,
        year: currentYear,
      };

      const normalizedCommodity = normalizeCommodityFilter(commodityName);
      if (normalizedCommodity) {
        baseFilter.commodity_name = normalizedCommodity;
      }

      const agentTokens =
        filter?.filter_agent_token?.length > 0
          ? filter.filter_agent_token
          : globalState?.agent_token
            ? [globalState.agent_token]
            : [];

      const promises = agentTokens.map(async (agentToken) => {
        const _filter = { ...baseFilter };
        if (filter?.filter_agent_token && filter?.filter_agent_token.length) {
          _filter.filter_agent_token = agentToken;
        }

        const apiClaims = {};
        if (globalState?.user_role !== USER_ROLE_ADMIN) {
          apiClaims.agent_token = globalState?.agent_token;
        }

        const rows = await UsersApiHelper.users_search("PositionSummary", _filter, apiClaims);
        return rows.map((row) => ({
          ...row,
          calc_percent_protected: computePercentProtected(row),
        }));
      });

      const allResults = await Promise.all(promises);
      // Distinct by producer + commodity (same key as grid row id)
      const distinctAllResults = _.uniqBy(allResults.flat(), getReportRowKey);
      const savedSort = getGridState()[RISK_SUMMARY_BROKER_GRID_KEY]?.sorting;

      setReportData(sortReportRows(distinctAllResults, savedSort));
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      setReportData(null);
      handleError(err);
    }
  };

  // ---

  const [filter, dispatchFilter] = useReducer(propUpdateReducer, {
    filter_agent_token: normalizeAgentTokens(
      globalState?.filter_agent_token?.length
        ? globalState.filter_agent_token
        : [globalState?.agent_token].filter(Boolean)
    ),
    search_producer_name: globalState?.search_text_portfolio,
  });

  const [commodityName, setCommodityName] = useState(() =>
    normalizeCommodityFilter(filter?.commodity_name || getStorageValue(STORAGE_KEY))
  );

  useEffect(() => {
    setStorageValue(STORAGE_KEY, commodityName);
  }, [commodityName]);

  const selectedBroker = useMemo(() => {
    const tokens = new Set(normalizeAgentTokens(filter?.filter_agent_token));
    return dedupeBrokersByToken(
      brokers?.filter((broker) => tokens.has(String(broker?.agent_token ?? "").toLowerCase())) || []
    );
  }, [brokers, filter?.filter_agent_token, globalState?.agent_token]);

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "filter_agent_token",
        value: filter?.filter_agent_token, //array
      },
    });
  }, [filter?.filter_agent_token]);

  const handleChangeAgentToken = async (agentTokens) => {
    dispatchFilter({
      type: "update",
      payload: {
        key: "filter_agent_token",
        value: normalizeAgentTokens(agentTokens.map((item) => item.agent_token)),
      },
    });
  };

  const filterHandleChangeState = (event, value) => {
    if (value == null && event.target.value != null) {
      value = event.target.value;
    }
    if (value != filter?.search_producer_name) {
      dispatchFilter({
        type: "update",
        payload: { key: event.target.name, value: value },
      });

      // Persist search text?
      if (event.target.name == "search_producer_name") {
        persistSearchText(event?.target?.value);
      }
    }
  };

  const persistSearchText = async (searchText) => {
    globalDispatch({
      type: "update",
      payload: {
        key: "search_text_portfolio",
        value: searchText,
      },
    });
  };

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  const brokerExportLabel = useMemo(
    () =>
      selectedBroker
        ?.map((broker) => getBrokerDisplayLabel(broker))
        .filter(Boolean)
        .join("; ") || "",
    [selectedBroker]
  );

  const [sortModel, setSortModel] = useState(() => {
    const savedSort = getGridState()[RISK_SUMMARY_BROKER_GRID_KEY]?.sorting;
    return savedSort ? [savedSort] : [];
  });

  const handleGridSortModelChange = useCallback(
    (model) => {
      setSortModel(model);
      setGridState(RISK_SUMMARY_BROKER_GRID_KEY, { sorting: model[0] || null });
    },
    [setGridState]
  );

  const filteredReportData = useMemo(() => {
    const search = filter?.search_producer_name?.toLowerCase();
    return reportData?.filter((row) => {
      if (search) {
        return (
          row.last_name?.toLowerCase().includes(search) ||
          row.first_name?.toLowerCase().includes(search) ||
          row.business_name?.toLowerCase().includes(search) ||
          row.producer_token?.toLowerCase().includes(search) ||
          formatProducerName(row)?.toLowerCase().includes(search)
        );
      }
      return true;
    });
  }, [reportData, filter?.search_producer_name]);

  const sortKey = sortModel[0] ? `${sortModel[0].field}:${sortModel[0].sort}` : "";

  const displayedReportData = useMemo(
    () => sortReportRows(filteredReportData ?? [], sortModel[0]),
    [filteredReportData, sortKey]
  );

  const handleExportClick = async () => {
    if (!filteredReportData?.length) {
      enqueueSnackbar("No data to export.", { variant: "warning" });
      return;
    }

    setIsExporting(true);
    try {
      await exportRiskSummaryBrokerWorkbook({
        rows: filteredReportData,
        year: currentYear,
        commodityName,
        brokerLabel: brokerExportLabel,
      });
    } catch (err) {
      console.warn(err);
      enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
    } finally {
      setIsExporting(false);
    }
  };

  // ---

  useEffect(() => {
    if (globalState?.agent_token || globalState?.user_role == USER_ROLE_ADMIN) {
      setIsLoadingBrokers(true);
      loadBrokers();
    }
  }, [globalState?.agent_token, globalState?.user_role]);

  useEffect(() => {
    if (currentYear && (filter?.filter_agent_token?.length || globalState?.agent_token)) {
      _loadReportData();
    }
  }, [currentYear, filter?.filter_agent_token, commodityName, globalState?.agent_token]);

  // --- --- ---

  const _formatProducerNameCallback = useCallback((row) => {
    return formatProducerName(row);
  }, []);

  // Memoize column definition
  const columns = useMemo(
    () => [
      {
        field: "producer_token",
        headerName: "Producer",
        minWidth: 200,
        valueGetter: (value, row) => {
          return _formatProducerNameCallback(row);
        },
        flex: 1,
      },
      {
        field: "commodity_name",
        headerName: "Commodity",
      },

      {
        field: "production",
        headerName: "Production",
        valueFormatter: (value) => formatNumber(value, 0),
        align: "right",
        headerAlign: "right",
      },
      {
        field: "production_guarantee",
        headerName: "Guarantee",
        valueFormatter: (value) => formatNumber(value, 0),
        align: "right",
        headerAlign: "right",
      },
      {
        field: "marketing_goal_units",
        headerName: "Goal",
        valueFormatter: (value) => formatNumber(value, 0),
        align: "right",
        headerAlign: "right",
      },
      {
        field: "unit_quantity_hedge_total",
        headerName: "Hedged",
        valueFormatter: (value) => formatNumber(value, 0),
        align: "right",
        headerAlign: "right",
      },
      {
        field: "calc_percent_protected",
        headerName: "% Protected",
        type: "number",
        // Beyond ±1000% show ∞ / -∞ so huge ratios don't render as giant numbers; sorting still uses the raw ratio.
        valueFormatter: (value) =>
          value == null ? null : value > 10 ? "∞" : value < -10 ? "-∞" : formatPercentage(value, 2),
        cellClassName: (params) => (Math.abs(params.value) > 10 ? "cell-infinity" : ""),
        minWidth: 150,
        align: "right",
        headerAlign: "right",
      },

      {
        field: "amount_cash",
        headerName: "Cash",
        valueFormatter: (value) => formatNumber(value, 0),
        cellClassName: "grid-column-group-a first",
      },
      {
        field: "amount_basis_only",
        headerName: "Basis",
        valueFormatter: (value) => formatNumber(value, 0),
        cellClassName: "grid-column-group-a",
      },
      {
        field: "amount_hta",
        headerName: "HTA",
        valueFormatter: (value) => formatNumber(value, 0),
        cellClassName: "grid-column-group-a",
      },

      {
        field: "unit_quantity_futures",
        headerName: "Futures",
        valueFormatter: (value) => formatNumber(value, 0),
        cellClassName: "grid-column-group-b first",
      },
      {
        field: "unit_quantity_long_put",
        headerName: "Long Puts",
        valueFormatter: (value) => formatNumber(value, 0),
        cellClassName: "grid-column-group-b",
      },
      {
        field: "unit_quantity_otc",
        headerName: "OTC",
        valueFormatter: (value) => formatNumber(value, 0),
        cellClassName: "grid-column-group-b",
      },

      {
        field: "unit_quantity_long_call",
        headerName: "Long Calls",
        valueFormatter: (value) => formatNumber(value, 0),
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        headerAlign: "right",
        align: "right",
        width: 100,
        sortable: false,
        getActions: (params) =>
          params.row.id !== "total"
            ? [
                <GridActionsCellItem
                  key="view"
                  icon={<EastOutlinedIcon />}
                  label="View Details"
                  onClick={() =>
                    navigate(
                      `/marketing/position_summary/${params?.row.producer_token}/${currentYear}/${params?.row?.commodity_name}`
                    )
                  }
                />,
              ]
            : [],
      },
    ],
    [navigate, currentYear]
  );

  return (
    <>
      <WorkspaceHeader
        title="Position Summary"
        category={{
          title: "Broker",
          icon: <BrokerIcon />,
        }}
      />
      <Grid container>
        <Grid item xs={12}>
          <Stack
            className="headerWithMenuButton"
            sx={{
              flexDirection: { xs: "column", md: "row" },
              alignItems: "start",
              gap: 2,
            }}
          >
            <Box
              className="position-summary-agent"
              sx={{
                width: { xs: "100%", md: "480px" },
                "& .MuiFormControl-root": {
                  width: "100% !important",
                },
              }}
            >
              <AgentAutoComplete brokers={brokers} selectedBroker={selectedBroker} onChange={handleChangeAgentToken} />
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "start",
                paddingTop: { md: "1.75rem" },
                margin: { md: "0 !important" },
              }}
            >
              <DropdownSelector value={currentYear} onChange={setCurrentYear} options={getBudgetYears()} />
            </Box>

            <Box
              className="position-summary-commodity"
              sx={{
                display: "flex",
                alignItems: "start",
              }}
            >
              <DataFormSelect
                value={commodityName}
                name="commodity_name"
                isRequired={false}
                disabled={isLoading}
                onChange={(event, value) => {
                  const normalizedValue = normalizeCommodityFilter(value);
                  if (commodityName !== normalizedValue) {
                    dispatchFilter({
                      type: "update",
                      payload: { key: event.target.name, value: normalizedValue },
                    });
                    setCommodityName(normalizedValue);
                  }
                }}
                objectNameLabel="Commodity"
                itemGetKey={(item) => item.commodity_name}
                itemGetLabel={(item) => item.commodity_name}
                doFetch={true}
                limitTags={isSmall ? 2 : 3}
                customDataLoader={() => RmaApiHelper.searchCommodity({}, {})}
              />
            </Box>

            {/* TODO: Add search icon */}
            <StatefulControlledTextField
              label="Search"
              name="search_producer_name"
              className="search-input"
              isRequired={false}
              defaultValue={filter?.search_producer_name || ""}
              onChange={filterHandleChangeState}
              InputLabelProps={{ shrink: true }}
              sx={{ margin: { md: "0 !important" } }}
            />

            {isLoading && (
              <Box sx={{ paddingTop: { md: "1.75rem" } }}>
                <CircularProgress />
              </Box>
            )}

            <Box sx={{ paddingTop: { md: "1.75rem" } }}>
              <Button
                variant="outlined"
                onClick={handleExportClick}
                disabled={isLoading || isExporting || !filteredReportData?.length}
                startIcon={isExporting ? <CircularProgress size={18} /> : <CloudDownloadOutlinedIcon />}
                sx={{ whiteSpace: "nowrap" }}
              >
                {isExporting ? "Exporting…" : "Export Excel"}
              </Button>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      <TilleyDataGrid
        rows={displayedReportData || []}
        columns={columns}
        pageSizeOptions={[5, 10, 15, 20, 25, 50, 100]}
        defaultPageSize={isMobile ? 5 : 15}
        gridKey={RISK_SUMMARY_BROKER_GRID_KEY}
        getRowId={getReportRowKey}
        sortModel={sortModel}
        onSortModelChange={handleGridSortModelChange}
        sortingMode="server"
        isLoading={isLoading}
      />
    </>
  );
}
